// EditableMetricCell.jsx — #57 BUILD item 2 (field_override affordance): inline edit on a
// displayed metric cell -> POST /overlay field_override; renders the override + an "edited"
// badge + the original value on hover + a revert control.
//
// DISPLAY-ONLY per the overlay contract (backend/services/pg_overlay_service.py docstring):
// this never feeds recompute. Real number corrections belong in ledger_poc_client_inputs ->
// project(), or fix-at-source; this is for correcting a display artifact (e.g. a mis-OCR'd cell)
// without touching the underlying stream.
import { useState } from "react";
import { Pencil, Check, X } from "lucide-react";
import { toast } from "sonner";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Textarea } from "@/components/ui/textarea";
import { addOverlay, findActiveOverlay, revertOverlay, NOTE_MAX_LEN } from "./overlayApi";

// The only golden cells the backend's override route accepts (mirrors routes/ledger.py's
// _OverrideFieldRequest.field docstring: nav | distributions_cumulative | commitment_total |
// capital_*_cumulative). Computed ratios (tvpi/dpi/irr/uncalled) are never overridable here.
export const OVERRIDABLE_FIELDS = new Set([
  "commitment_total",
  "capital_contributed_cumulative",
  "distributions_cumulative",
  "nav",
]);

export default function EditableMetricCell({
  field,
  streamId,
  token,
  editable,
  rawValue,
  formattedValue,
  extractedRawValue,
  overridden,
  onApplied,
}) {
  const [open, setOpen] = useState(false);
  const [inputValue, setInputValue] = useState(rawValue == null ? "" : String(rawValue));
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const canEdit = editable && !!streamId && OVERRIDABLE_FIELDS.has(field);

  if (!canEdit) {
    return (
      <td className="py-3 px-3 text-xs text-right font-mono text-gray-700 whitespace-nowrap">
        {formattedValue}
      </td>
    );
  }

  async function handleSave(e) {
    e.preventDefault();
    const parsed = inputValue === "" ? null : parseFloat(inputValue);
    if (inputValue !== "" && !Number.isFinite(parsed)) {
      toast.error("Enter a valid number");
      return;
    }
    setBusy(true);
    try {
      await addOverlay({
        streamId,
        kind: "field_override",
        payload: { field, value: parsed },
        note,
        token,
      });
      toast.success("Value corrected");
      setOpen(false);
      setNote("");
      if (onApplied) onApplied();
    } catch (err) {
      toast.error("Correction failed: " + (err?.message || "error"));
    } finally {
      setBusy(false);
    }
  }

  async function handleRevert() {
    setBusy(true);
    try {
      const overlay = await findActiveOverlay(streamId, "field_override", token, { field });
      if (overlay) {
        await revertOverlay(overlay.id, token);
        toast.success("Reverted to the extracted value");
        if (onApplied) onApplied();
      } else {
        toast.info("No active correction found to revert");
      }
    } catch (err) {
      toast.error("Revert failed: " + (err?.message || "error"));
    } finally {
      setBusy(false);
    }
  }

  return (
    <td className="py-3 px-3 text-xs text-right font-mono text-gray-700 whitespace-nowrap group">
      <span className="inline-flex items-center gap-1 justify-end">
        {overridden ? (
          <span
            title={
              extractedRawValue != null
                ? `Originally ${extractedRawValue} — click to revert`
                : "Click to revert"
            }
            data-testid={`edited-badge-${field}`}
            className="cursor-pointer inline-flex items-center gap-1 px-1 rounded bg-blue-50 border border-blue-200 text-blue-700"
            onClick={(e) => {
              e.stopPropagation();
              handleRevert();
            }}
          >
            {formattedValue}
            <span className="text-[9px] uppercase tracking-wide">edited</span>
          </span>
        ) : (
          <span>{formattedValue}</span>
        )}

        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setInputValue(rawValue == null ? "" : String(rawValue));
                setOpen(true);
              }}
              className="opacity-0 group-hover:opacity-100 focus:opacity-100 p-0.5 rounded hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-opacity"
              aria-label={`Edit ${field}`}
              data-testid={`edit-field-${field}`}
            >
              <Pencil className="w-3 h-3" />
            </button>
          </PopoverTrigger>
          <PopoverContent align="end" className="w-64" onClick={(e) => e.stopPropagation()}>
            <form onSubmit={handleSave} className="space-y-2">
              <p className="text-[10px] uppercase tracking-wider font-semibold text-gray-400">
                Correct this value
              </p>
              <input
                type="number"
                step="any"
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                disabled={busy}
                className="h-7 w-full px-2 rounded border border-gray-300 bg-white text-xs text-gray-700 focus:outline-none focus:ring-1 focus:ring-indigo-300"
                aria-label={`New value for ${field}`}
              />
              <Textarea
                value={note}
                onChange={(e) => setNote(e.target.value.slice(0, NOTE_MAX_LEN))}
                maxLength={NOTE_MAX_LEN}
                placeholder="Note (optional)"
                disabled={busy}
                className="text-xs min-h-[50px]"
                aria-label="Note"
              />
              <div className="flex items-center justify-end gap-1.5">
                <button
                  type="button"
                  onClick={() => setOpen(false)}
                  disabled={busy}
                  className="inline-flex items-center gap-1 px-2 py-1 rounded text-[11px] text-gray-500 hover:text-gray-700 disabled:opacity-50"
                >
                  <X className="w-3 h-3" /> Cancel
                </button>
                <button
                  type="submit"
                  disabled={busy}
                  className="inline-flex items-center gap-1 px-2 py-1 rounded text-[11px] font-medium bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50"
                >
                  <Check className="w-3 h-3" /> {busy ? "Saving…" : "Save"}
                </button>
              </div>
            </form>
          </PopoverContent>
        </Popover>
      </span>
    </td>
  );
}
